import { useSyncExternalStore } from 'react';
import { ringFor } from './calls';
import { myChoiceNow, useMessagesLive, type LiveEvent } from './messages';

/**
 * A coworker asking this person to join a meeting going on now (Joey, Nov 3 2026: meetings should
 * feel like a Patch): the same ring and card as a Patch, for half a minute, with Join and Not now.
 * The server leaves out people on do not disturb; this checks again here. Their phone gets a
 * notification too (the server's push, tag meeting:<id>).
 */
export interface MeetingRing {
  /** "<tenant>|<meeting id>". */
  key: string;
  tenant: string;
  meetingId: string;
  title: string;
  /** The link's path (/m/<code>). */
  path: string;
  by: { id: string; name: string };
  /** When it stops, on this device's clock. */
  until: number;
}

const RING_MS = 30_000;
let rings: MeetingRing[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, number>();

function sync(next: MeetingRing[]): void {
  const before = new Set(rings.map((r) => r.key));
  const after = new Set(next.map((r) => r.key));
  rings = next;
  for (const k of before) if (!after.has(k)) ringFor(`meeting:${k}`, false);
  for (const k of after) if (!before.has(k)) ringFor(`meeting:${k}`, true);
  for (const l of [...listeners]) l();
}

export function useMeetingRings(): MeetingRing[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => rings,
    () => rings
  );
}

/** Stops one ringing (its card goes too). */
export function stopMeetingRing(key: string): void {
  window.clearTimeout(timers.get(key));
  timers.delete(key);
  if (rings.some((r) => r.key === key)) sync(rings.filter((r) => r.key !== key));
}

/** The ring an event starts for this person, if any: not their own invite, not too late, not on do not disturb. */
export function meetingRingOf(t: string, me: string, e: LiveEvent, ctx: { dnd: boolean; now: number }): MeetingRing | null {
  const m = e.meeting;
  if (e.type !== 'meeting' || !m || m.by.id === me || ctx.dnd) return null;
  const left = Math.min(RING_MS, Date.parse(m.until) - ctx.now);
  if (!(left > 1500)) return null;
  return { key: `${t}|${m.id}`, tenant: t, meetingId: m.id, title: m.title, path: m.path, by: m.by, until: ctx.now + left };
}

/** For the company's people while Stitch is open: a coworker asking them into a meeting. */
export function useMeetingRingLive(tenant: string, myId: string | undefined, on: boolean): void {
  const t = tenant.toLowerCase();
  useMessagesLive(
    (e) => {
      if (e.type === 'reset') {
        for (const r of rings) if (r.tenant === t) stopMeetingRing(r.key);
        return;
      }
      if (e.type !== 'meeting' || e.tenant?.toLowerCase() !== t || !myId) return;
      const ring = meetingRingOf(t, myId, e, { dnd: myChoiceNow(t) === 'dnd', now: Date.now() });
      if (!ring) return;
      stopMeetingRing(ring.key);
      sync([ring, ...rings].slice(0, 3));
      timers.set(
        ring.key,
        window.setTimeout(() => stopMeetingRing(ring.key), ring.until - Date.now())
      );
    },
    on && Boolean(t) && Boolean(myId)
  );
}

if (typeof window !== 'undefined') {
  window.addEventListener('stitch:signed-out', () => {
    for (const r of rings) stopMeetingRing(r.key);
  });
}
